import { useState, useMemo } from 'react'
import { ListItem, ListItemText, Checkbox, Typography, Box, Collapse, useTheme, useMediaQuery, lighten, darken } from '@mui/material'
import { TaskItemStatusChip } from './task-item-status-chip'
import { TpTooltip } from '~/components'
import { useOrgContextDispatch, useOrgHoverContext, setHoveredTask } from '~/common/context/organization/org-context'
import { useCropsContextDispatch, taskEdited } from '~/common/context/crops/crops-context'
import { useUpdateTask } from '~/api/supabase-mutations'
import { useCurrentUser } from '~/common/use-current-user'
import { useTaskPhaseReport } from '~/crop/crop-schedule/hooks/use-task-phase-report'
import { formatPSQLInterval } from '../utils/task-interval-utils'
import { useIsSeasonReadOnly } from '~/farm/use-is-season-read-only'
import { useSnackbar } from 'notistack'
import { useTimelineStyles } from '~/crop/crop-schedule/hooks/use-timeline-styles'
import { useTasksContext, useTasksContextDispatch, setSelectedTask } from '../context/tasks-context'
import { TaskItemDetail } from './task-item-detail'

// Transition delay before task moves to new position (ms)
const COMPLETION_TRANSITION_DELAY = 400
const MAX_DENSE_CROP_NAME_CHAR = 20

/**
 * Individual task row component
 *
 * Displays:
 * - Checkbox (completed state)
 * - Task type name
 * - Status chip
 * - Date, duration, location, notes
 *
 * @param {Object} task - Task object
 * @param {Function} onUpdateTask - Callback to update task in parent state
 * @param {boolean} canUnComplete - Whether completed tasks can be unchecked (default: true)
 */
export const TaskItem = ({ task, onUpdateTask, canUnComplete = true }) => {
  const dispatch = useOrgContextDispatch()
  const { hoveredTask } = useOrgHoverContext()
  const cropsDispatch = useCropsContextDispatch()
  const tasksDispatch = useTasksContextDispatch()
  const { selectedTaskId } = useTasksContext()
  const { updateTask } = useUpdateTask()
  const { currentUser } = useCurrentUser()
  const { reportPhaseTransitionOnComplete } = useTaskPhaseReport()
  const seasonReadOnly = useIsSeasonReadOnly()
  const { getTaskStyle } = useTimelineStyles()
  const { enqueueSnackbar } = useSnackbar()
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [transitionTarget, setTransitionTarget] = useState(null) // 'completed' | 'ready' | null

  const theme = useTheme()
  const isDense = useMediaQuery(theme.breakpoints.down('xl'))
  const backgroundEffect = theme.palette.mode === 'dark' ? darken : lighten

  const isCompleted = task.state === 'completed'
  const isScheduled = task.state === 'scheduled'
  const isCompletedAndLocked = isCompleted && !canUnComplete
  const isExpanded = selectedTaskId === task.id
  const isHovered = hoveredTask === task.id

  const formattedDate = (new Date(task.task_window_start)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })

  const formattedDuration = formatPSQLInterval(task.duration)

  const compactDate = useMemo(() =>
    new Date(task.task_window_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    [task.task_window_start]
  )

  const compactDuration = useMemo(() =>
    formatPSQLInterval(task.duration)
      .replace(/(\d+)\s+weeks?/, '$1w')
      .replace(/(\d+)\s+days?/, '$1d')
      .replace(/(\d+)\s+months?/, '$1mo'),
    [task.duration]
  )

  const compactTaskType = useMemo(() => {
    if (!task.task_types || !task.task_types.name) return ''
    return task.task_types.name
      .replace(/Outdoor/g, 'Out.')
      .replace(/Indoor/g, 'In.')
      .replace(/Preparation/g, 'Prep.')
      .replace(/Winter/g, '')
      .replace(/Compost\/Breakdown/g, 'Compost')
  }, [task.task_types])

  const compactCropLabel = useMemo(() => {
    const name = task.crop?.name
    if (!name) return null
    return name.length > MAX_DENSE_CROP_NAME_CHAR ? name.slice(0, 25) + '…' : name
  }, [task.crop])

  const handleTaskSubmit = async (event) => {
    const checked = event.target.checked

    // Determine if completing or uncompleting
    const isCompleting = checked
    const targetState = isCompleting ? 'completed' : 'ready'
    const updates = isCompleting
      ? {
          state: 'completed',
          completed_by: currentUser.id,
          completed_at: new Date().toISOString(),
        }
      : {
          state: 'ready',
          completed_by: null,
          completed_at: null,
        }

    // Start transition animation
    setIsTransitioning(true)
    setTransitionTarget(targetState)

    // Delay the optimistic update to allow visual transition
    setTimeout(() => {
      const optimisticUpdates = {
        ...updates,
        task_states: {
          ...task.task_states,
          name: isCompleting ? 'Completed' : 'Ready'
        }
      }

      // Update task list (local component state)
      if (onUpdateTask) {
        onUpdateTask(task.id, optimisticUpdates)
      }

      // Broadcast to timeline and other listeners (via crops context)
      cropsDispatch(taskEdited(task.id, { submissions: optimisticUpdates }))

      // Clear transition state
      setIsTransitioning(false)
      setTransitionTarget(null)
    }, COMPLETION_TRANSITION_DELAY)

    // Send update to server (mutation hook handles cache invalidation)
    await updateTask(task.id, updates, {
      onSuccess: (updatedTask) => {
        // Update with full server response (includes proper task_states join)
        if (onUpdateTask && updatedTask) {
          onUpdateTask(task.id, updatedTask)
        }
        // If completing a phase-linked task, cascade the phase boundary report
        if (isCompleting) {
          reportPhaseTransitionOnComplete(task)
          enqueueSnackbar('Task completed — crop schedule updated', { variant: 'success' })
        } else {
          enqueueSnackbar('Task marked as ready', { variant: 'info' })
        }
      },
      onError: () => {
        // Clear transition state on error
        setIsTransitioning(false)
        setTransitionTarget(null)

        // Revert optimistic update on error
        if (onUpdateTask) {
          onUpdateTask(task.id, {
            state: task.state,
            completed_by: task.completed_by,
            completed_at: task.completed_at,
            task_states: task.task_states
          })
        }
      }
    })
  }

  const handleRowClick = () => {
    tasksDispatch(setSelectedTask({ taskId: task.id }))
  }

  const taskStyle = getTaskStyle(task.task_types.code)

  return (
    <>
      <ListItem
        sx={{
          p: 0,
          border: isHovered ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
          '&:hover': { border: `1px solid ${theme.palette.primary.main}` },
          // Transition styling
          opacity: isTransitioning ? 0.6 : 1,
          backgroundColor: isTransitioning
            ? transitionTarget === 'completed'
              ? 'success.50'
              : 'info.50'
            : backgroundEffect(taskStyle.color, 0.65),
          transition: 'opacity 300ms ease-in-out, background-color 300ms ease-in-out',
        }}
        onMouseEnter={() => dispatch(setHoveredTask({ hoveredTask: task.id }))}
        onMouseLeave={() => dispatch(setHoveredTask({ hoveredTask: null }))}
      >
        <TpTooltip maxWidth={250} title={
          isCompletedAndLocked ? "Can't uncomplete this task" : isScheduled ? "Task is scheduled and not ready yet. You can set the ready window in Preferences > User section" : ""
        }>
          <Box>
            <Checkbox
              checked={isTransitioning ? transitionTarget === 'completed' : isCompleted}
              disabled={isCompletedAndLocked || isTransitioning || isScheduled || seasonReadOnly}
              onChange={handleTaskSubmit}
            />
          </Box>
        </TpTooltip>

        <ListItemText
          onClick={handleRowClick}
          sx={{ cursor: 'pointer' }}
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1">
                {task.crop ? '🌾 ' : '🏚️ '}
                {task.crop ? isDense ? compactCropLabel : task.crop.name : task.organization.name}
              </Typography>
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', mr: 2 }}>
                <TaskItemStatusChip
                  state={task.state}
                  stateName={task.task_states.name}
                />
              </Box>
            </Box>
          }
          secondary={
            <Box component="span" sx={{ display: 'block', ml: 1, mb: 0.25 }}>
              <Typography variant="caption" component="span" sx={{ display: 'block', lineHeight: 1.4 }}>
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, rowGap: 0.5, flexWrap: isDense ? 'nowrap' : 'wrap', overflow: isDense ? 'hidden' : 'visible' }}>
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', gap: 0.5 }}>
                    📅
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                      {isDense ? compactDate : formattedDate}
                    </Box>
                  </Box>
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', gap: 0.5 }}>
                    ⏱️
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                      {isDense ? compactDuration : formattedDuration}
                    </Box>
                  </Box>
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', gap: 0.5 }}>
                    {taskStyle.icon}
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                      <strong>{isDense ? compactTaskType : task.task_types.name}</strong>
                    </Box>
                  </Box>
                </Box>
              </Typography>
            </Box>
          }
        />
      </ListItem>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            backgroundColor: backgroundEffect(taskStyle.color, 0.82),
            borderBottom: `1px solid ${theme.palette.divider}`,
            pl: 6,
            pr: 2,
            py: 1,
          }}
        >
          <TaskItemDetail task={task} />
        </Box>
      </Collapse>
    </>
  )
}
